"use client";

import { useState, useRef, useEffect } from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, X, Menu, ChevronLast } from "lucide-react";
import { BACKEND_URL } from "@/config";
import axios from "axios";
import { useCurrentProfile, useToken } from "@/global_state";
import { useNavigate } from "react-router-dom";
import { use } from "i18next";
import { Profile } from "@/types";
import { respondToMatching } from "@/utils";

// Mock data for user profiles

export default function DatingApp() {
  const [authToken, setAuthToken] = useToken();
  const [currentProfile, setCurrentProfile] = useCurrentProfile();
  const [exitX, setExitX] = useState(0);
  const [showMatch, setShowMatch] = useState(false);
  const navigate = useNavigate();

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-30, 30]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]);

  const cardRef = useRef<HTMLDivElement>(null);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);

  const fetchProfiles = async () => {
    const response = await axios.get(
      `${BACKEND_URL}/api/users/matching-profiles/`
    );
    const newProfiles = response.data.results;
    if (newProfiles.length > 0) {
      setCurrentProfile(newProfiles[0]);
      setProfiles(response.data.results.slice(1));
    } else {
      setCurrentProfile(null);
      setProfiles([]);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchProfiles();
    }
  }, [authToken]);

  const handleClickProfile = () => {
    if (currentProfile) {
      navigate(`/app/profiles/${currentProfile.id}`);
    }
  };

  const handleSwipe = (response: "like" | "dislike" | "skip") => {
    const horizontalMultiplier = response === "like" ? 1 : -1;
    setExitX(200 * horizontalMultiplier);
    respondToMatching(
      currentProfile?.id || "",
      response,
      authToken || "",
      (res) => {
        if (response === "like" && res.data.match) {
          setShowMatch(true);
          setTimeout(() => setShowMatch(false), 2000);
        }
      }
    );

    setTimeout(() => {
      if (profiles && profiles.length > 0) {
        setCurrentProfile(profiles[0]);
        setProfiles(profiles.slice(1));
      } else {
        setCurrentProfile(null);
        setProfiles([]);
      }
      setExitX(0);
    }, 300);
  };

  const handleDragEnd = (event: any, info: any) => {
    if (info.offset.x < -100) {
      handleSwipe("dislike");
    } else if (info.offset.x > 100) {
      handleSwipe("like");
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <main className="flex-grow flex flex-col items-center justify-center p-4">
        {profiles === null && (
          <div className="text-center">
            <span className="text-4xl font-bold mb-4">Loading profiles...</span>
          </div>
        )}
        {profiles?.length === 0 && currentProfile === null && (
          <div className="text-center">
            <span className="text-xl font-bold mb-4">
              No more profiles found. <br />
              Try changing your preferences or come back later.
            </span>
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentProfile?.id}
            initial={{ opacity: 0, x: exitX }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: exitX }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-sm relative"
          >
            <motion.div
              ref={cardRef}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              style={{ x, rotate }}
              onDragEnd={handleDragEnd}
              onClick={handleClickProfile}
              className="w-full cursor-grab active:cursor-grabbing"
            >
              <Card
                className="bg-gray-800 border-gray-700"
                style={currentProfile ? {} : { display: "none" }}
              >
                <CardContent className="p-0 relative">
                  <div
                    className="w-full h-[400px] object-cover rounded-t-lg"
                    style={{
                      backgroundImage: `url(${currentProfile?.image})`,
                      backgroundSize: "cover",
                    }}
                  />
                  <div className="p-4">
                    <h2 className="text-2xl font-semibold text-gray-50">
                      {currentProfile?.name}, {currentProfile?.age}
                    </h2>
                    <p className="text-gray-400 mt-2">{currentProfile?.bio}</p>
                  </div>
                  <motion.div
                    className="absolute top-4 left-4 bg-green-500 rounded-full p-2"
                    style={{ opacity: useTransform(x, [0, 100], [0, 1]) }}
                  >
                    <Heart className="h-8 w-8 text-white" />
                  </motion.div>
                  <motion.div
                    className="absolute top-4 right-4 bg-red-500 rounded-full p-2"
                    style={{ opacity: useTransform(x, [-100, 0], [1, 0]) }}
                  >
                    <X className="h-8 w-8 text-white" />
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        <div
          className="flex justify-center mt-6 space-x-4"
          style={currentProfile ? {} : { display: "none" }}
        >
          <Button
            onClick={() => handleSwipe("dislike")}
            size="lg"
            variant="outline"
            className="rounded-full p-4 border-gray-700 hover:bg-gray-700"
          >
            <X className="h-8 w-8 text-red-500" />
            <span className="sr-only">Dislike</span>
          </Button>

          <Button
            onClick={() => handleSwipe("skip")}
            size="lg"
            variant="outline"
            className="rounded-full p-4 border-gray-700 hover:bg-gray-700"
          >
            <ChevronLast className="h-8 w-8 text-gray-500" />
            <span className="sr-only">Skip</span>
          </Button>
          <Button
            onClick={() => handleSwipe("like")}
            size="lg"
            variant="outline"
            className="rounded-full p-4 border-gray-700 hover:bg-gray-700"
          >
            <Heart className="h-8 w-8 text-pink-500" />
            <span className="sr-only">Like</span>
          </Button>
        </div>

        <AnimatePresence>
          {showMatch && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="fixed inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="bg-pink-500 text-white px-6 py-3 rounded-full text-2xl font-bold shadow-lg">
                You like each other!
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
